import { ChangeDetectionStrategy, Component, HostListener, Inject, InjectionToken, Input, OnInit } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Item, getOriginFromUrl } from "@gtmhub/plugins/components/plugin/plugin.component";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { PluginCommonService } from "@webapp/plugins/components/plugin/services/plugin-common.service";
import { PluginOkrsService } from "@webapp/plugins/components/plugin/services/plugin-okrs.service";
import { PluginPeopleService } from "@webapp/plugins/components/plugin/services/plugin-people.service";
import { PluginStateService } from "@webapp/plugins/components/plugin/services/plugin-state.service";
import { PluginTasksService } from "@webapp/plugins/components/plugin/services/plugin-tasks.service";
import { PluginService } from "@webapp/plugins/components/plugin/services/plugin.service";
import { Plugin, PluginContext, PluginSize, UIPluginAttachPoint } from "@webapp/plugins/plugins.models";

const PROVIDERS_TOKEN: InjectionToken<PluginService[]> = new InjectionToken<PluginService[]>("");
const pluginSizeMap = {
  goalForm: {
    width: 678,
    height: 400,
  },
  metricForm: {
    width: 460,
    height: 400,
  },
};

@Component({
  selector: "plugin",
  templateUrl: "plugin.component.html",
  styleUrls: ["plugin.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyService,
    PluginStateService,
    { provide: PROVIDERS_TOKEN, useClass: PluginTasksService, multi: true },
    { provide: PROVIDERS_TOKEN, useClass: PluginCommonService, multi: true },
    { provide: PROVIDERS_TOKEN, useClass: PluginPeopleService, multi: true },
    { provide: PROVIDERS_TOKEN, useClass: PluginOkrsService, multi: true },
  ],
})
export class PluginComponent implements OnInit {
  constructor(private sanitizer: DomSanitizer, private pluginStateService: PluginStateService, @Inject(PROVIDERS_TOKEN) private providers: PluginService[]) {}

  @Input()
  public accessPoint: UIPluginAttachPoint | string;
  @Input()
  public context: PluginContext | string;
  @Input()
  public item: Item;
  @Input()
  public plugin: Plugin;

  public src: SafeResourceUrl;
  public pluginId: string;
  public pluginSize: { width: number; height: number };

  public ngOnInit(): void {
    this.pluginId = `${this.accessPoint}-${this.plugin.uiPlugin.src}`;
    this.src = this.sanitizer.bypassSecurityTrustResourceUrl(this.plugin.uiPlugin.src);

    this.pluginSize = PluginComponent.getPluginSize(this.accessPoint);
    this.pluginStateService.setPlugin(this.plugin);
    this.pluginStateService.setContext(this.context);
    this.pluginStateService.setItem(this.item);
    this.pluginStateService.setAccessPoint(this.accessPoint);
  }

  @HostListener("window:message", ["$event"])
  public onMessage(event: MessageEvent): void {
    /** each plugin should listen to the specific plugin events, ensure that the plugin domain matches when listening/sending events */

    if (event.origin !== getOriginFromUrl(this.plugin.uiPlugin.src)) {
      return;
    }

    let executed = false;

    const { type, data } = event.data;

    for (const service of this.providers) {
      const serviceImpl = Object.getPrototypeOf(service);

      if (serviceImpl[type]) {
        service[type](type, data);
        executed = true;
        break;
      }
    }

    if (executed) {
      return;
    } else {
      throw new Error(`Method with type: ${type} is not implemented in plugin service providers.`);
    }
  }

  private static getPluginSize(accessPoint: UIPluginAttachPoint | string): PluginSize {
    return pluginSizeMap[accessPoint];
  }
}
